import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Phone, ArrowRight } from "lucide-react";
import { ASSETS, CLINIC } from "@/lib/assets";

const ease = [0.76, 0, 0.24, 1];

const MaskedLine = ({ children, delay = 0, className = "" }) => (
    <span className={`mask-line ${className}`}>
        <motion.span
            className="inline-block will-change-transform"
            initial={{ y: "110%", rotate: 3 }}
            animate={{ y: "0%", rotate: 0 }}
            transition={{ duration: 1.1, ease, delay }}
        >
            {children}
        </motion.span>
    </span>
);

const SunMark = ({ className = "", duration = 60 }) => (
    <motion.svg
        className={className}
        viewBox="0 0 200 200"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1, rotate: 360 }}
        transition={{
            opacity: { duration: 1.2, delay: 0.6, ease },
            scale: { duration: 1.2, delay: 0.6, ease },
            rotate: { duration, repeat: Infinity, ease: "linear" },
        }}
    >
        {Array.from({ length: 24 }).map((_, i) => (
            <line
                key={i}
                x1="100"
                y1="10"
                x2="100"
                y2="26"
                stroke="#FF5A00"
                strokeWidth="2"
                strokeLinecap="round"
                transform={`rotate(${i * 15} 100 100)`}
            />
        ))}
        <circle cx="100" cy="100" r="42" fill="none" stroke="#FF5A00" strokeWidth="1.2" />
        <circle cx="100" cy="100" r="6" fill="#FF5A00" />
    </motion.svg>
);

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
    const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
    const titleY = useTransform(scrollYProgress, [0, 1], ["0%", "-10%"]);
    const sunY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);

    return (
        <section
            id="top"
            ref={ref}
            className="relative min-h-[100svh] w-full overflow-hidden pt-24 md:pt-28 pb-14 md:pb-16"
            data-testid="hero-section"
        >
            {/* Decorative sun */}
            <motion.div
                style={{ y: sunY }}
                className="pointer-events-none absolute right-[-25vw] top-[10vh] sm:right-[-14vw] md:right-[-4vw] md:top-[8vh] w-[80vw] sm:w-[55vw] md:w-[38vw] aspect-square opacity-[0.11] md:opacity-[0.14] z-0"
                aria-hidden="true"
            >
                <SunMark className="w-full h-full" duration={80} />
            </motion.div>

            {/* Top metadata */}
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.35, ease }}
                className="mx-auto max-w-[1600px] px-5 md:px-10 grid grid-cols-12 gap-3 md:gap-4 mt-2 md:mt-4 relative z-[2]"
            >
                <div className="col-span-6 md:col-span-3 label text-surya-muted text-[0.6rem] md:text-xs">
                    (S/01) — Est. Ranchi
                </div>
                <div className="hidden md:block col-span-6 label text-surya-muted">
                    Periodontology · Oral Implantology · Cosmetic Care
                </div>
                <div className="col-span-6 md:col-span-3 text-right label text-surya-muted text-[0.6rem] md:text-xs">
                    <span className="inline-flex items-center gap-2">
                        <span className="h-1.5 w-1.5 rounded-full bg-surya-orange animate-pulse" />
                        Accepting patients
                    </span>
                </div>
            </motion.div>

            {/* Main grid */}
            <div className="mx-auto max-w-[1600px] px-5 md:px-10 mt-8 md:mt-14 grid grid-cols-12 gap-6 md:gap-10 items-end relative z-[2]">
                {/* LEFT — text */}
                <motion.div
                    style={{ y: titleY }}
                    className="col-span-12 md:col-span-7 lg:col-span-8 order-2 md:order-1"
                >
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4, duration: 0.6, ease }}
                        className="label text-surya-orange mb-4 md:mb-8 flex items-center gap-3"
                    >
                        <span className="h-px w-8 md:w-10 bg-surya-orange" />
                        Dr. Shikha Sinha · BDS, MDS
                    </motion.div>

                    <h1
                        className="font-serif text-surya-ink tracking-[-0.035em] leading-[0.9] text-[clamp(2.6rem,9.5vw,10.5rem)] font-light"
                        data-testid="hero-title"
                    >
                        <MaskedLine delay={0.5}>A quieter kind</MaskedLine>
                        <MaskedLine delay={0.62} className="pl-[6vw] md:pl-[10vw]">
                            of{" "}
                            <span className="italic-display text-surya-orange">
                                dentistry.
                            </span>
                        </MaskedLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 1.05, ease }}
                        className="mt-6 md:mt-10 max-w-xl text-surya-muted text-sm md:text-lg leading-relaxed"
                    >
                        Ranchi&apos;s calm, precision-led practice — specialising in{" "}
                        <span className="text-surya-ink">periodontal care</span> and{" "}
                        <span className="text-surya-ink">oral implantology</span>.
                        Modern equipment, evidence-based protocols, and a chair
                        you&apos;ll actually want to return to.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 1.25, ease }}
                        className="mt-8 md:mt-12 flex flex-wrap items-center gap-3 md:gap-4"
                    >
                        <a
                            href={CLINIC.whatsapp}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-cta-button"
                            className="group inline-flex items-center gap-3 rounded-full bg-surya-orange text-white hover:bg-surya-orangeHover pl-5 md:pl-6 pr-4 md:pr-5 py-3.5 md:py-4 label"
                        >
                            <span>Book a Consultation</span>
                            <span className="inline-flex h-6 w-6 md:h-7 md:w-7 items-center justify-center rounded-full bg-white text-surya-orange group-hover:bg-surya-ink group-hover:text-white">
                                <ArrowRight className="h-3 w-3 md:h-3.5 md:w-3.5" strokeWidth={1.5} />
                            </span>
                        </a>
                        <a
                            href={`tel:${CLINIC.phoneE164}`}
                            data-testid="hero-phone"
                            className="group inline-flex items-center gap-2 md:gap-3 border border-surya-line px-4 md:pl-5 md:pr-5 py-3.5 md:py-4 rounded-full label text-surya-ink hover:border-surya-orange hover:text-surya-orange"
                        >
                            <Phone className="h-3.5 w-3.5 md:h-4 md:w-4" strokeWidth={1.2} />
                            {CLINIC.phone}
                        </a>
                    </motion.div>
                </motion.div>

                {/* RIGHT — portrait */}
                <div className="col-span-12 md:col-span-5 lg:col-span-4 order-1 md:order-2">
                    <motion.div
                        initial={{ clipPath: "inset(30% 15% 30% 15%)", opacity: 0 }}
                        animate={{ clipPath: "inset(0% 0% 0% 0%)", opacity: 1 }}
                        transition={{ duration: 1.4, delay: 0.85, ease }}
                        className="relative aspect-[4/5] md:aspect-[3/4] w-full max-w-[480px] mx-auto md:max-w-none overflow-hidden bg-surya-surface border border-surya-line"
                    >
                        <motion.img
                            src={ASSETS.doctor}
                            alt="Dr. Shikha Sinha at Surya Dental Care"
                            style={{ y: imgY, scale: imgScale }}
                            className="editorial-img absolute inset-0 h-full w-full object-cover"
                            data-testid="hero-parallax-image"
                            loading="eager"
                            decoding="async"
                        />
                        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between label text-white/95 mix-blend-difference text-[0.6rem] md:text-xs">
                            <span>Dr. Shikha Sinha</span>
                            <span>Ranchi · 2026</span>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1.35, ease }}
                        className="mt-3 md:mt-4 flex items-start justify-between label text-surya-muted text-[0.6rem] md:text-xs"
                    >
                        <span>Fig. 00 — Consultation</span>
                        <span className="text-right">
                            Periodontology
                            <br />
                            Oral Implantology
                        </span>
                    </motion.div>
                </div>
            </div>

            {/* Stats strip */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 1.5, ease }}
                className="mx-auto max-w-[1600px] px-5 md:px-10 mt-12 md:mt-20 grid grid-cols-2 md:grid-cols-4 gap-x-4 md:gap-x-6 gap-y-8 border-t border-surya-line pt-8 md:pt-10 relative z-[2]"
                data-testid="hero-stats"
            >
                {CLINIC.stats.map((s, i) => (
                    <div key={i} className="flex flex-col">
                        <div className="font-serif font-light text-surya-ink text-3xl md:text-5xl leading-none tracking-[-0.02em]">
                            {s.value.split("").map((ch, k) => (
                                <span
                                    key={k}
                                    className={/[a-zA-Z+]/.test(ch) ? "italic-display text-surya-orange" : ""}
                                >
                                    {ch}
                                </span>
                            ))}
                        </div>
                        <div className="label text-surya-muted mt-2 md:mt-3 text-[0.6rem] md:text-xs leading-snug">
                            {s.label}
                        </div>
                    </div>
                ))}
            </motion.div>
        </section>
    );
};
